import { clean } from './quinn';
import { captionOf } from './lightbox';
import { byDist, miles, poiDesc, travel, typeWords, type Poi } from './places';
import { createStore } from './store';

export type GalleryCat = 'homes' | 'community' | 'neighborhood';
export const GALLERY_CATS: GalleryCat[] = ['homes', 'community', 'neighborhood'];
export const CAT_LABEL: Record<GalleryCat, string> = { homes: 'Homes', community: 'Community', neighborhood: 'Neighborhood' };

export interface GalleryTile {
  src: string;
  alt: string;
  caption: string;
  desc: string;
  /** Short facts beside the category ("Coffee shop · 5 min walk"). */
  meta: string;
  cat: GalleryCat;
}

interface Photo {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
  description?: string;
}

/** MultiHub titles photos "Apartment" (inside a home) or "Community"; untitled ones are read from the alt text. */
const INTERIOR = /kitchen|bedroom|bathroom|living (room|area)|dining|closet|home office|laundry|hallway|staircase|primary suite|nursery|interior/i;
const photoCat = (g: Photo): GalleryCat =>
  /^apartment|^home|^interior/i.test(g.title || '') ? 'homes' : /^community|^amenit/i.test(g.title || '') ? 'community' : INTERIOR.test(g.alt_text || '') ? 'homes' : 'community';

const FILLER = / of a modern apartment complex| at a multifamily property/gi;
const sentence = (s: string) => (s ? s[0].toUpperCase() + s.slice(1).replace(/\.?$/, '.') : '');

/** The detail the caption leaves out: the photo description, else the alt text past "with", else the whole alt text. */
function detailOf(g: Photo, caption: string): string {
  const d = clean(g.description);
  if (d && d !== caption) return d;
  const alt = (g.alt_text || '').replace(FILLER, '').trim().replace(/\.$/, '');
  if (!alt || alt === caption) return '';
  const cut = alt.indexOf(' with ');
  return sentence(cut >= 0 && alt.slice(0, cut).trim() === caption ? alt.slice(cut + 6).trim() : alt);
}

/**
 * Auto-generated alt text opens with a bland label ("A kitchen", "A spacious kitchen", "A two-story house"):
 * any article-led phrase of up to four words, or a single bare word.
 */
export function isGeneric(s: string): boolean {
  const words = s.replace(/[.!]+$/, '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  return /^(an?|the)$/i.test(words[0]) ? words.length <= 5 : words.length === 1;
}

const LEAD = /\s+(?:with|featuring|showcasing|showing|boasting|including|offering|that (?:has|have|features))\s+|,\s+/i;
const upper = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : '');

/**
 * Caption is always set (the lightbox title). Desc is the specific detail only; generic text never lands there,
 * so a tile's hover may have none. "A spacious kitchen featuring white cabinets" → "A spacious kitchen" / "White cabinets."
 */
export function photoTiles(list: Photo[]): GalleryTile[] {
  return list
    .map((g) => ({ g, src: clean(g.image) || clean(g.original_url) || '' }))
    .filter((x) => x.src)
    .map(({ g, src }) => {
      const raw = captionOf(g);
      const m = LEAD.exec(raw);
      const split = !!m && isGeneric(raw.slice(0, m.index));
      const caption = upper(split ? raw.slice(0, m!.index).trim() : raw);
      let desc = split && !clean(g.description) ? sentence(raw.slice(m!.index + m![0].length).trim().replace(/\.$/, '')) : detailOf(g, raw);
      if (isGeneric(desc) || desc.replace(/\.$/, '') === caption) desc = '';
      return { src, alt: g.alt_text || '', caption, desc, meta: '', cat: photoCat(g) };
    });
}

/** Neighborhood places with their own photo, nearest first. */
export function placeTiles(pois: Poi[]): GalleryTile[] {
  return pois
    .filter((x) => clean(x.poi_featured_image))
    .slice()
    .sort(byDist)
    .map((x) => {
      const mi = miles(x);
      const meta = [typeWords(x.poi_place_primary_type || x.poi_type), travel(x) || (mi != null ? `${mi.toFixed(1)} mi away` : '')].filter(Boolean).join(' · ');
      return { src: x.poi_featured_image!, alt: x.poi_name || '', caption: x.poi_name || '', desc: poiDesc(x), meta, cat: 'neighborhood' as const };
    });
}

export interface GalleryState {
  cat: GalleryCat | 'all';
}

export const galleryStore = createStore<GalleryState>({ cat: 'all' });

/** Jump links ("Homes ↓") use #homes / #community / #neighborhood; the filter bar is the scroll target. */
export function catFromHash(hash: string): GalleryCat | null {
  const h = hash.replace(/^#/, '') as GalleryCat;
  return GALLERY_CATS.includes(h) ? h : null;
}
